import {
  CreditConsentSchema,
  CreditContactSchema,
  CreditIdentitySchema,
  SsnSchema,
  TEXAS_LICENSE_NUMBER_RE,
  UNDERTAKING_VERSION_PLACEHOLDER,
  normalizeTexasLicenseNumber,
  type CreditCheckRequest,
  type FunnelSnapshotResponse,
  type MoneyWire,
} from '@rheora/enrollment-contract';

import { dateOfBirthErrorKey } from '../schemas/dob.schema';
import { isVerificationStaleFor } from './email-verification.service';
import { isNanpNumber } from './leadContact.service';

/**
 * Identity/credit form logic (E-SF05) — kept OUT of the `.tsx` per CLAUDE.md.
 *
 * ── SSN NEVER LEAVES THIS FLOW BY ANY SIDE DOOR ────────────────────────────
 * The SSN lives only in the CreditPage's local `useState` while the form is
 * filled, is passed as a mutation VARIABLE (never a TanStack query key, so it
 * cannot land in the query cache), and is discarded after the call. This service
 * is pure: it never logs, never stores, and never formats the SSN back out.
 * Validation reuses the shared contract Zod schema so the FE and BFF agree on
 * what a well-formed identity is.
 *
 * ── ADV-2 SCOPED EXCEPTION (recorded) ──────────────────────────────────────
 * The FE convention is React Hook Form + Zod via `@fluxora/forms`. That package
 * is a non-functional stub in this workspace (not Controller-bound, and
 * react-hook-form is not a dependency of US-Shop), so E-SF02/E-SF03 already use
 * the `useState` + this-service Zod-validator pattern. E-SF05 follows the same
 * precedent — Zod IS the validator here, it is simply not driven by RHF. See the
 * E-SF05 epic doc §ADV-2.
 */

export interface IdentityForm {
  readonly firstName: string;
  readonly lastName: string;
  /** YYYY-MM-DD (the date input's native value). */
  readonly dateOfBirth: string;
  /**
   * Raw as typed; digits are extracted on validate. Never stored/logged.
   *
   * ⚠️ NO LONGER THE ONLY WAY TO IDENTIFY YOURSELF. The form takes an SSN, a
   * driver licence, or both, and requires at least one — see
   * `validateIdentityForm`. An empty string here is only an error when the
   * licence fields are empty too.
   */
  readonly ssn: string;
  /**
   * The Texas driver licence / state ID number, as typed. Digits only, capped at
   * 8, on every keystroke (`sanitizeDriversLicenseInput`).
   *
   * ⚠️ NO ISSUING-STATE FIELD, BY DESIGN. This storefront serves Texas (ERCOT)
   * only, so the jurisdiction is not a question worth asking — it is always TX,
   * and the request says so. That is what lets the field enforce the Texas format
   * from the first keystroke instead of waiting to learn which rules apply.
   */
  readonly driversLicenseNumber: string;
  /**
   * Contact email (E-SF06 blocker 1). Explicit + required for enrollment — the
   * account is created from it at sign. Contact PII, not the SSN tier.
   */
  readonly email: string;
  /** Contact phone as typed; digits extracted on validate (NANP 10 digits). */
  readonly phone: string;
  /** FCRA §604 authorization to obtain a consumer report. */
  readonly fcraAuthorization: boolean;
  /** PUCT §25.478 deposit-disclosure acknowledgement. */
  readonly puctDepositDisclosure: boolean;
  /**
   * The customer's undertaking that everything above is accurate (E-SF05).
   *
   * ⚠️ A SEPARATE TICK FROM THE FCRA AUTHORIZATION, DELIBERATELY. Folding it into
   * the FCRA box would dilute a regulated authorization into a general-purpose
   * catch-all, and an FCRA consent that also means three other things is arguably no
   * FCRA consent at all. Two boxes, two records, two versions.
   */
  readonly informationAccuracyUndertaking: boolean;
  /**
   * The NORMALISED email address proven by the E-SF05 OTP, or null when nothing has
   * been proven yet.
   *
   * ⚠️ AN ADDRESS, NOT A BOOLEAN, AND THAT IS THE WHOLE DESIGN. A boolean
   * `emailVerified` survives the customer editing the email field, so the SPA would
   * enable Continue for an address the server has never seen proven — and the server
   * gate (which is the real control) would `400` the credit check. Storing WHICH
   * address was proven makes "verified, then edited" detectable, which is what
   * `isVerificationStaleFor` reports below.
   */
  readonly verifiedEmail: string | null;
}

export const emptyIdentityForm: IdentityForm = {
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  ssn: '',
  driversLicenseNumber: '',
  email: '',
  phone: '',
  fcraAuthorization: false,
  puctDepositDisclosure: false,
  informationAccuracyUndertaking: false,
  verifiedEmail: null,
};

export type IdentityFormErrors = Partial<Record<keyof IdentityForm, string>>;

/**
 * ── WHICH CREDIT FIELDS SURVIVE A BROWSER REFRESH ───────────────────────────
 *
 * `GET /bff/funnel` restores the funnel, so this step needs an answer to "what does
 * the form look like for a returning tab?". The answer is deliberately almost
 * nothing, and the decision lives HERE rather than in the page because it is a
 * privacy decision, not a rendering one — a reviewer must be able to read it in one
 * place, with a test per field.
 *
 * SURVIVES:
 *   `email` + `verifiedEmail` — the address the E-SF05 OTP has PROVEN. This is the
 *     whole reason the snapshot carries it: without it a refresh forces a fresh OTP
 *     send, spending one of only three per hour on BOTH the session ledger and the
 *     victim-side recipient cap. So pressing F5 would cost the customer a third of
 *     their hourly budget. Both fields are set from the same value so the
 *     "is this still the address you proved?" check agrees with itself on arrival.
 *
 * DOES NOT SURVIVE, each for a stated reason:
 *   `ssn`      — ⚠️ NEVER persisted ANYWHERE, by anyone: not on the server, not in
 *                storage, not in a query key, not in a log. It is EXPECTED to vanish
 *                on refresh, and that is the promise this page makes the customer.
 *   `dateOfBirth` — the BFF's boot-time response guard forbids `dateOfBirth`/`dob` on
 *                every response, with no per-route exception. Not a limitation to
 *                work around; the customer retypes it.
 *   `dateOfBirth` — see above. It is the one field a customer may expect back and
 *                does not get, so the notice tells them so explicitly.
 *   the three CONSENTS — an FCRA authorization is an affirmative act. Pre-ticking one
 *                because the customer ticked it before a refresh would be recording a
 *                consent nobody gave in this sitting.
 *
 * ALSO SURVIVES (amended 2026-07-30):
 *   `firstName`/`lastName`/`phone` — from `snapshot.identity`. These were initially
 *     excluded on the reasoning that since the SSN and DOB must be retyped anyway,
 *     restoring the rest bought little. That was wrong in practice: a refresh that
 *     cleared the customer's own name, email and phone read as total data loss, so
 *     they now come back. The BFF's two session-store comments were amended to match
 *     — this is a reversal of a documented invariant, not an oversight.
 *
 * ── AND THE SAME FOUR FIELDS CARRY THE OFFER PREFILL (E-SF16 W6) ───────────
 * A customer who arrived from an agent's emailed offer gets `identity` and
 * `verifiedEmail` populated on their FIRST snapshot, because `GET /bff/offer/:token`
 * seeded the credit step's own session namespaces server-side. THIS FUNCTION NEEDS
 * NO CHANGE FOR THAT AND MUST NOT ACQUIRE ONE: `snapshot.offer` stays render-only,
 * and reading the name or the address from it as well would give one value two
 * owners — which is how the two drift and how the stale one wins.
 *
 * ⚠️ `identity.firstName`/`lastName` may therefore be a name an AGENT captured
 * rather than one this customer typed. That is exactly why the BFF refuses to split
 * an ambiguous one (`offer-identity-seed.ts`): what lands here becomes the OFAC
 * `fullName` and the ESIGN `signerName` unless the customer edits it.
 */
export function seedIdentityFormFrom(snapshot: FunnelSnapshotResponse | undefined): IdentityForm {
  if (snapshot === undefined) return emptyIdentityForm;
  const verifiedEmail = snapshot.verifiedEmail;
  return {
    ...emptyIdentityForm,
    // Both fields from one value, so the "is this still the address you proved?"
    // check agrees with itself on arrival. With no proven address, the email the
    // customer typed at the contact step prefills the FIELD ONLY — `verifiedEmail`
    // stays null, so the OTP gate treats it exactly like a freshly typed address.
    ...(verifiedEmail === null
      ? { email: snapshot.identity?.email ?? '' }
      : { email: verifiedEmail, verifiedEmail }),
    firstName: snapshot.identity?.firstName ?? '',
    lastName: snapshot.identity?.lastName ?? '',
    phone: snapshot.identity?.phone ?? '',
  };
}

/**
 * WHICH prefilled-form notice to show, or null for none.
 *
 *   `REFRESH` — a returning tab. The proven address came back from the server so
 *               another OTP is not needed; the DOB and SSN did not.
 *   `OFFER`   — the customer arrived from an agent's emailed offer. The name, the
 *               phone and the proven address were seeded server-side from the
 *               quote; the DOB and SSN still have to be typed.
 *
 * ── WHY TWO VARIANTS AND NOT ONE ───────────────────────────────────────────
 * The REFRESH copy says "we picked up where you left off" and "there is no need to
 * request ANOTHER code". Both are false for someone who has never been here and
 * was never sent a code — and this is a regulated form, where a notice that
 * misdescribes what happened is worse than no notice. Two variants, two strings.
 *
 * ⚠️ THE `OFFER` COPY MUST NOT ASSERT *HOW* THE ADDRESS WAS CONFIRMED. An offer
 * session whose link carried no recipient proves nothing at entry, so that
 * customer runs a normal OTP and then sees this same notice — "confirmed because
 * you clicked our link" would be a lie in exactly that case. It says the address
 * is confirmed, which is true on both routes in.
 *
 * ── WHY IT IS A PREDICATE ON THE *FORM*, NOT ON THE SNAPSHOT ────────────────
 * Keying it to the snapshot would leave the notice on screen for the rest of the
 * session, telling a customer who has already retyped everything that they still
 * need to. Keying it to the form means it explains the gap it is describing and
 * then gets out of the way as soon as that gap is closed.
 *
 * ⚠️ IT TESTS THE DOB AND THE IDENTIFIERS — AND *ONLY* THOSE. It used to require the name
 * fields to be empty too, which was harmless while nothing prefilled them: an
 * offer arrival seeds a name, so the notice went SILENT for exactly the customer
 * who most needed it and was left with two blank fields and no explanation. The
 * fields the notice is ABOUT are the two that did not survive.
 */
export type RehydrateNoticeKind = 'REFRESH' | 'OFFER';

export function rehydrateNoticeFor(
  form: IdentityForm,
  /** True when this session was seeded from an agent's offer (`snapshot.offer`). */
  fromOffer: boolean,
): RehydrateNoticeKind | null {
  // Both variants describe a CONFIRMED address. Without one there is nothing to
  // reassure the customer about and the OTP field speaks for itself.
  if (form.verifiedEmail === null) return null;
  // The driver licence counts alongside the SSN: it is the other identifier the
  // customer may have typed, and it does not come back on the rehydrate response
  // either, so a returning tab with a blank licence field needs the same
  // explanation a blank SSN field does.
  if (
    form.dateOfBirth.length > 0 ||
    form.ssn.length > 0 ||
    form.driversLicenseNumber.length > 0
  ) {
    return null;
  }
  return fromOffer ? 'OFFER' : 'REFRESH';
}

/** The i18n key pair for a notice. Exhaustive — a new variant cannot fall through. */
export function rehydrateNoticeKeys(kind: RehydrateNoticeKind): {
  readonly title: string;
  readonly body: string;
} {
  switch (kind) {
    case 'REFRESH':
      return { title: 'shop.credit.rehydrated.title', body: 'shop.credit.rehydrated.body' };
    case 'OFFER':
      return { title: 'shop.credit.prefilled.title', body: 'shop.credit.prefilled.body' };
  }
}

export interface IdentityValidation {
  readonly ok: boolean;
  readonly errors: IdentityFormErrors;
  /**
   * The identity+contact+consent slice of a CreditCheckRequest — candidateId/planId
   * are added by the page.
   */
  readonly value: Pick<CreditCheckRequest, 'identity' | 'contact' | 'consent'> | undefined;
}

/** Digits only. The FE strips separators the customer may type (dashes, spaces). */
export function normalizeSsn(raw: string): string {
  return raw.replace(/\D/gu, '');
}

/**
 * What the SSN field is ALLOWED TO CONTAIN as the customer types: digits only,
 * capped at 9.
 *
 * ── WHY SANITISE ON INPUT AND NOT ONLY VALIDATE ON SUBMIT ──────────────────
 * `validateSsn` below reports a bad value *after* the customer presses the button,
 * and `maxLength` bounds the character count but not the character SET — so a field
 * with only those two accepts `12-34-abc!!` and says nothing until submit. For a
 * fixed-width numeric secret that is just a worse experience: the field is masked
 * (`type="password"`), so the customer cannot even see what they typed to spot the
 * stray character.
 *
 * A paste of `123-45-6789` becomes `123456789` rather than being rejected, which is
 * the behaviour someone pasting from a document expects.
 *
 * This does NOT replace `validateSsn` — too-short is still only knowable at submit,
 * and the BFF re-parses the contract schema regardless. It removes the ways a value
 * can be wrong that the customer should never have been allowed to create.
 */
export function sanitizeSsnInput(raw: string): string {
  return normalizeSsn(raw).slice(0, 9);
}

/**
 * Exactly 9 digits — no more, no fewer.
 *
 * ── THE CONTRACT SCHEMA IS THE ARBITER, NOT A SECOND REGEX HERE ─────────────
 * `SsnSchema` is `/^\d{9}$/u`, and the BFF re-parses the same schema on
 * `POST /bff/credit/check`. Delegating to it means the FE cannot drift
 * into accepting something the server will reject (or worse, rejecting something it
 * would accept) — the same reason the email branch below defers to
 * `CreditContactSchema.shape.email`. A locally written `/^\d{9}$/` would be a second
 * copy of a rule that already has an owner.
 *
 * Separators are stripped FIRST, so `123-45-6789` and `123 45 6789` are both the
 * same nine digits. TOO MANY digits gets its own message: "enter a valid SSN" is
 * unhelpful when the real problem is that the customer typed ten, and a mistyped
 * SSN is a failed credit pull on the highest-PII endpoint in the funnel.
 *
 * @returns an i18n KEY (resolved by the page), or `undefined` when valid.
 */
export function validateSsn(raw: string): string | undefined {
  const digits = normalizeSsn(raw);
  if (digits.length === 0) return 'shop.credit.form.ssn.required';
  if (digits.length > 9) return 'shop.credit.form.ssn.tooLong';
  if (!SsnSchema.safeParse(digits).success) {
    return 'shop.credit.form.ssn.invalid';
  }
  return undefined;
}

/**
 * What the DRIVER LICENCE field is ALLOWED TO CONTAIN as the customer types:
 * digits only, capped at 8 — the Texas DPS format.
 *
 * ── WHY CONSTRAIN AS YOU TYPE RATHER THAN ONLY VALIDATE ────────────────────
 * The field cannot hold a value the rule would then reject, so a customer never
 * sees an error about a character the field accepted a moment earlier. A licence
 * transcribed as `1234 5678` becomes `12345678` rather than being refused.
 *
 * This does NOT replace `validateDriversLicense`: too-short is still only
 * knowable when they leave the field, and the BFF re-parses the contract schema
 * regardless.
 */
export function sanitizeDriversLicenseInput(raw: string): string {
  return raw.replace(/\D/gu, '').slice(0, 8);
}

/**
 * The number as it will travel: sanitised for the jurisdiction, then normalised.
 *
 * Texas 7-digit legacy licences are LEFT-PADDED to the modern 8 (the padding rule
 * itself lives in the contract, so the FE and the BFF cannot disagree about
 * whether `1234567` is a licence). Everywhere else the sanitised value is already
 * canonical.
 *
 * ⚠️ IT TAKES THE **RAW** INPUT, AND PADS ONLY WHAT WAS ALREADY ALL DIGITS.
 * Padding the sanitised value alone is silently dangerous: someone with Texas
 * selected who pastes a California licence (`A1234567`) has the letter stripped,
 * leaving seven digits — which would then be padded to `01234567` and submitted
 * as a perfectly well-formed TEXAS licence belonging to somebody else entirely.
 * The legacy convention is for numbers that were SHORT, not for numbers that were
 * shortened. So a raw value containing a letter is never padded; it stays seven
 * digits and fails the 8-digit rule, which is the honest answer.
 */
export function driversLicenseForWire(raw: string): string {
  const sanitized = sanitizeDriversLicenseInput(raw);
  if (/[A-Za-z]/u.test(raw)) return sanitized;
  return normalizeTexasLicenseNumber(sanitized);
}

/** Digits only — strips (), -, spaces, and a leading US country code (+1 / 1). */
export function normalizePhone(raw: string): string {
  const digits = raw.replace(/\D/gu, '');
  return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
}

/** The licence pair's errors, keyed by form field. Empty when nothing is typed. */
export type DriversLicenseErrors = Pick<IdentityFormErrors, 'driversLicenseNumber'>;

/**
 * Validate the licence ON ITS OWN, from the raw field value.
 *
 * ── WHY IT IS SEPARATE FROM `validateIdentityForm` ─────────────────────────
 * The page runs this on BLUR, so a customer who mistypes a licence is told while
 * they are still looking at the field. Waiting for submit is far too late here:
 * `Run credit check` is disabled until the email is proven and three consents are
 * ticked, so a format error on a field near the top of the form would otherwise
 * stay invisible through several minutes of work.
 *
 * Nothing typed → no error. "You must supply an identifier" is the FORM's
 * question, not this field's, and it is asked once, at submit.
 */
export function validateDriversLicense(rawNumber: string): DriversLicenseErrors {
  const number = driversLicenseForWire(rawNumber);
  if (number.length === 0) return {};
  return TEXAS_LICENSE_NUMBER_RE.test(number)
    ? {}
    : { driversLicenseNumber: 'shop.credit.form.driversLicense.texas' };
}

/**
 * Validate the identity form. Field errors are i18n KEYS (resolved by the page).
 * `asOf` is injected so the adult-age check is deterministic in tests.
 */
export function validateIdentityForm(
  form: IdentityForm,
  asOf: Date = new Date(),
  options: { readonly skipEmailGate?: boolean } = {},
): IdentityValidation {
  const errors: IdentityFormErrors = {};

  if (form.firstName.trim().length === 0) errors.firstName = 'shop.credit.form.firstName.required';
  if (form.lastName.trim().length === 0) errors.lastName = 'shop.credit.form.lastName.required';

  // Shape, real-date, 18+ and plausibility all come from ONE arbiter — the same
  // `schemas/dob.schema` the calendar's `max` is derived from — so a bound the picker
  // greys out and a bound this rejects can never describe different days. It reports
  // an i18n key per failure mode rather than one catch-all: "you must be at least 18"
  // was, until 2026-08-04, also what a customer saw for a mistyped year like 1090.
  const dobError = dateOfBirthErrorKey(form.dateOfBirth, asOf);
  if (dobError !== undefined) errors.dateOfBirth = dobError;

  // ── AT LEAST ONE IDENTIFIER: AN SSN, A LICENCE, OR BOTH ──────────────────
  // Each supplied identifier is validated on its own terms; the "did you give us
  // anything at all?" question is asked once, afterwards. Doing it in that order
  // matters: asking first would report "enter an SSN or a licence" to someone who
  // HAS typed a licence and merely mistyped it, which reads as the form ignoring
  // what they just did.
  const ssn = normalizeSsn(form.ssn);
  const licenseNumber = driversLicenseForWire(form.driversLicenseNumber);
  const hasSsn = ssn.length > 0;
  const startedLicense = licenseNumber.length > 0;

  if (hasSsn) {
    const ssnError = validateSsn(form.ssn);
    if (ssnError !== undefined) errors.ssn = ssnError;
  }

  Object.assign(errors, validateDriversLicense(form.driversLicenseNumber));

  // Neither route taken. ONE message, on the SSN field, naming BOTH ways forward
  // — repeating it under the licence field would make one missing answer look
  // like two separate mistakes.
  if (!hasSsn && !startedLicense) {
    errors.ssn = 'shop.credit.form.identity.required';
  }

  // Contact (E-SF06 blocker 1) — explicit + required. Email format + NANP phone.
  const email = form.email.trim();
  if (email.length === 0) {
    errors.email = 'shop.credit.form.email.required';
  } else if (!CreditContactSchema.shape.email.safeParse(email).success) {
    errors.email = 'shop.credit.form.email.invalid';
  }
  // E-SF05 email OTP — the address must be PROVEN, and proven for THIS address.
  // Only reported once the address is otherwise well-formed: telling someone who has
  // typed nothing to "verify your email" is noise on top of "enter your email".
  //
  // ⚠️ This is a CONVENIENCE, not the control. `POST /bff/credit/check` refuses an
  // unproven address server-side, where a caller who never runs our JavaScript
  // cannot skip it. Never weaken that gate on the strength of this check.
  // `skipEmailGate` is the DEV-ONLY bypass (VITE_FEATURE_SKIP_EMAIL_VERIFICATION);
  // the BFF's server-side gate carries its own copy of the flag, so the two
  // layers cannot disagree about whether verification is required.
  if (errors.email === undefined && options.skipEmailGate !== true) {
    if (form.verifiedEmail === null) {
      errors.email = 'shop.credit.form.email.unverified';
    } else if (isVerificationStaleFor(form.verifiedEmail, form.email)) {
      errors.email = 'shop.credit.form.email.changed';
    }
  }

  const phone = normalizePhone(form.phone);
  if (form.phone.trim().length === 0) {
    errors.phone = 'shop.credit.form.phone.required';
  } else if (phone.length !== 10 || !isNanpNumber(phone)) {
    // Ten digits is not enough: an area code or exchange starting 0 or 1 is
    // a number no US carrier can issue. Same rule as the contact step.
    errors.phone = 'shop.credit.form.phone.invalid';
  }

  if (!form.fcraAuthorization) errors.fcraAuthorization = 'shop.credit.consent.fcra.required';
  if (!form.puctDepositDisclosure) errors.puctDepositDisclosure = 'shop.credit.consent.puct.required';
  if (!form.informationAccuracyUndertaking) {
    errors.informationAccuracyUndertaking = 'shop.credit.consent.undertaking.required';
  }

  if (Object.keys(errors).length > 0) return { ok: false, errors, value: undefined };

  // Belt-and-braces: the shared contract schema is the final arbiter — including
  // its own at-least-one rule, so the check above and the wire format cannot drift
  // into disagreeing about what a usable identity is.
  //
  // Absent identifiers are OMITTED rather than sent as '': the contract's fields
  // are `.optional()`, and an empty string is a value that fails the regex, so
  // spreading conditionally is the difference between "no licence" and "a licence
  // that is blank".
  const identity = CreditIdentitySchema.safeParse({
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    dateOfBirth: form.dateOfBirth,
    ...(hasSsn ? { ssn } : {}),
    // ⚠️ ALWAYS TEXAS. The storefront serves ERCOT only, so the jurisdiction is
    // not asked — but it is still SENT, because a licence number without one
    // names nobody: they are unique only within the issuing state, and an
    // operator reading the record downstream needs to know which.
    ...(startedLicense
      ? { driversLicense: { number: licenseNumber, issuingState: 'TX' } }
      : {}),
    identityKind: 'SSN',
  });
  const contact = CreditContactSchema.safeParse({ email, phone });
  const consent = CreditConsentSchema.safeParse({
    fcraAuthorization: form.fcraAuthorization,
    puctDepositDisclosure: form.puctDepositDisclosure,
    informationAccuracyUndertaking: form.informationAccuracyUndertaking,
    // ⚠️ The version stamp of the copy the customer ACTUALLY SAW — an acceptance
    // recorded against no version is an acceptance of nothing in particular. It is
    // sent, never rendered: a version identifier on screen is noise to a customer and
    // an invitation to treat the undertaking as boilerplate. When counsel signs off,
    // the constant bumps in the same change as the copy.
    undertakingVersion: UNDERTAKING_VERSION_PLACEHOLDER,
  });
  if (!identity.success || !contact.success || !consent.success) {
    // Should be unreachable — every field above was checked against these same
    // schemas. The fallback message names whichever identifier the customer
    // actually attempted, rather than always blaming the SSN as it used to.
    const fallback: IdentityFormErrors = hasSsn
      ? { ssn: 'shop.credit.form.ssn.invalid' }
      : startedLicense
        ? { driversLicenseNumber: 'shop.credit.form.driversLicense.texas' }
        : { ssn: 'shop.credit.form.identity.required' };
    return { ok: false, errors: fallback, value: undefined };
  }

  return {
    ok: true,
    errors: {},
    value: { identity: identity.data, contact: contact.data, consent: consent.data },
  };
}

/**
 * Are all three consent acknowledgements ticked?
 *
 * Named and exported so the DISABLED BUTTON and the REASON shown beside it read the
 * same predicate. Two places deciding "is consent complete" is how the button and
 * its explanation drift apart, and a button whose stated reason disagrees with its
 * own disabled state is worse than one with no reason at all.
 *
 * Deliberately does NOT re-derive from `CreditConsentSchema`: that schema keeps
 * `informationAccuracyUndertaking` optional on purpose during its two-release
 * rollout (see the contract), so parsing it would let an unticked undertaking pass.
 * This is the UI's own, stricter question — may the customer press the button — and
 * it wants all three regardless of what the wire currently tolerates.
 */
export function hasAllCreditConsents(form: IdentityForm): boolean {
  return (
    form.fcraAuthorization &&
    form.puctDepositDisclosure &&
    form.informationAccuracyUndertaking
  );
}

/**
 * May the credit check be SUBMITTED at all? The E-SF05 preconditions the button
 * itself must reflect, so the customer is never invited to press a button whose only
 * possible outcome is a server refusal:
 *
 *   - the email address must be PROVEN, and proven for the address in the field now;
 *   - ALL THREE consent acknowledgements must be ticked.
 *
 * The remaining field-level problems are NOT folded in here on purpose: those are
 * reported by `validateIdentityForm` when the customer presses the button, which is
 * where an error message is useful. A permanently-disabled button with no explanation
 * is worse than a press that explains what is missing. These are different — each has
 * its own visible affordance next to it (the OTP field; the three checkboxes), so a
 * disabled button is legible rather than mysterious.
 *
 * ── WHY ALL THREE, NOT JUST THE UNDERTAKING ────────────────────────────────
 * This gated on `informationAccuracyUndertaking` ALONE, so ticking only the third
 * box enabled "Run credit check". The rationale above was already the argument
 * against that — the FCRA and PUCT boxes have exactly the same visible affordance
 * as the third one — so the omission read as an oversight rather than a choice.
 *
 * ⚠️ WHAT IT WAS *NOT*: an unauthorised consumer-report pull. `validateIdentityForm`
 * parses `CreditConsentSchema`, whose `fcraAuthorization` and `puctDepositDisclosure`
 * are `z.literal(true)`, so the press failed client-side before any request, and the
 * BFF re-enforces the same schema. The damage was a button that invited a press no
 * amount of correct typing could satisfy. Do not downgrade the gate again on the
 * grounds that "the schema catches it" — a control the customer cannot see is not a
 * control the customer can act on.
 */
/**
 * WHY "Run credit check" is disabled — or `null` when it is not.
 *
 * ── THE DEFECT ─────────────────────────────────────────────────────────────
 * A customer filled in every field, ticked every box, and the button stayed
 * dead. The reason was that their email was not OTP-proven, and NOTHING on the
 * screen said so: the OTP control sits further up the form, already looking
 * complete once a code has been sent, so the eye reads "all done" and the button
 * reads "no". A disabled control with no stated reason is a dead end — the
 * customer has no way to discover which of a dozen inputs is the problem.
 *
 * ── IT NAMES THE CONDITION, NOT THE STATE ──────────────────────────────────
 * "Form invalid" or "verification required" describe OUR model. The customer
 * needs the next physical action, so each blocker's copy points at the control
 * that clears it ("Email me a code", the acknowledgements above).
 *
 * ── ONE PREDICATE, TWO READERS ─────────────────────────────────────────────
 * ⚠️ `canSubmitIdentity` is DEFINED as "no blocker", so the button's disabled
 * state and the sentence under it cannot disagree. Do not re-implement either
 * side independently: a button that is disabled while its own explanation says
 * everything is fine is worse than no explanation, because it reads as a bug in
 * the page rather than as something the customer can fix.
 *
 * Reading order decides which is named when both block. The email control is
 * above the acknowledgements, so it is reported first — the customer is sent
 * back up the page once, not bounced between two ends of it.
 */
export type CreditSubmitBlocker = 'EMAIL_UNVERIFIED' | 'CONSENTS_INCOMPLETE';

export function creditSubmitBlocker(
  form: IdentityForm,
  options: { readonly skipEmailGate?: boolean } = {},
): CreditSubmitBlocker | null {
  // Covers both "never proven" and "proven, then the address was edited" — a
  // stale proof is not a proof, and the customer must re-request a code.
  // `skipEmailGate` is the DEV-ONLY bypass (VITE_FEATURE_SKIP_EMAIL_VERIFICATION);
  // the BFF enforces its own copy of this gate, so the flag only works when the
  // server agrees.
  if (
    options.skipEmailGate !== true &&
    (form.verifiedEmail === null || isVerificationStaleFor(form.verifiedEmail, form.email))
  ) {
    return 'EMAIL_UNVERIFIED';
  }
  if (!hasAllCreditConsents(form)) return 'CONSENTS_INCOMPLETE';
  return null;
}

/** The i18n key explaining a blocker. Exhaustive — a new blocker must not fall through. */
export function creditSubmitBlockerKey(blocker: CreditSubmitBlocker): string {
  switch (blocker) {
    case 'EMAIL_UNVERIFIED':
      return 'shop.credit.blocked.emailUnverified';
    case 'CONSENTS_INCOMPLETE':
      return 'shop.credit.blocked.consents';
  }
}

export function canSubmitIdentity(form: IdentityForm): boolean {
  return creditSubmitBlocker(form) === null;
}

/**
 * Format a versioned money amount for display, using exact BigInt integer math —
 * NEVER a float (`parseFloat`/Number would lose cents on large values). Deposit
 * amounts are small, but the discipline is the point.
 */
export function formatMoney(money: MoneyWire): string {
  const minor = BigInt(money.amount_minor);
  const negative = minor < 0n;
  const abs = negative ? -minor : minor;
  const divisor = 10n ** BigInt(money.scale);
  const whole = abs / divisor;
  const frac = (abs % divisor).toString().padStart(money.scale, '0');
  const symbol = money.currency === 'USD' ? '$' : `${money.currency} `;
  return `${negative ? '-' : ''}${symbol}${whole.toString()}${money.scale > 0 ? `.${frac}` : ''}`;
}
